// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { fontConfig } from "@pace/XDL/PreStyle/typography";
import { xScreen } from "@pace/XDL/Utilities";
import * as cssLinter from "css";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    imageAlt = "imageAlt",
    backgroundColor = "backgroundColor",
    tileTitle = "tileTitle",
    minimumHeight = "minimumHeight",
    tileText = "tileText",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    imagePositioning = "imagePositioning",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.imageAlt]: string;
        [fields.backgroundColor]: string;
        [fields.tileTitle]: string;
        [fields.tileText]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.imagePositioning]: "right" | "left";
        [fields.customCSS]: string;
    };
}

export interface ImageTextTileStyles {
    wrapper?: InjectableCss;
    contentSection?: InjectableCss;
    tileTitle?: TypographyProps;
    tileCopyText?: TypographyProps;
    imageSection?: InjectableCss;
    image?: LazyImageProps;
    tileButton?: ButtonPresentationProps;
}

export const imageTextTileStyles: ImageTextTileStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            ${xScreen("lte", "md")} {
                flex-direction: column;
            }
        `,
    },

    contentSection: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 42px 22px;
            width: 100%;
            &&& {
                ${xScreen("lte", "md")} {
                    flex-direction: column;
                    padding: 32px 16px;
                    order: 1;
                }
            }
        `,
    },
    imageSection: {
        css: css`
            position: relative;
        `,
    },
    image: {
        css: css`
            object-fit: cover;
            max-width: 100%;
            max-height: 100%;
            margin: auto;
            width: 543px;
            height: 100%;
            img {
                border-radius: 0 4px 4px 0;
                height: 100%;
            }
            &&& {
                ${xScreen("lte", "md")} {
                    height: 400px;
                    width: 100%;
                    img {
                        height: 100%;
                    }
                }
                ${xScreen("lte", "xs")} {
                    height: 200px;
                    width: 100%;
                }
            }
        `,
    },
    tileButton: {
        sizeVariant: "large",
        css: css`
            align-self: start;
        `,
    },
    tileTitle: {
        variant: "h1",
        color: "text.main",
        css: css`
            margin-bottom: 16px;
            text-align: left;
        `,
    },
    tileCopyText: {
        forwardAs: "p",
        color: "text.main",
        lineHeight: "26px",
        weight: fontConfig.weight.regular,
        css: css`
            margin-bottom: 15px;
            text-align: left;
            font-size: 16px;
        `,
    },
};

export const ImageTextTile: FC<OwnProps> = ({ fields, id }) => {
    const { url, title } = useGetLink(fields.buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const styles = imageTextTileStyles;

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };
    const backgroundStyles = `background-color: ${fields.backgroundColor};`;
    const imagePosition = fields.imagePositioning === "right" ? `order:0;` : `order:1;`;

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
        `,
    };
    const contentSectionStyles = {
        css: css`
            ${styles.contentSection?.css || ""}
            ${imagePosition}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="wrapper" {...wrapperStyles}>
                <StyledWrapper className="content-section" {...contentSectionStyles}>
                    {fields.tileTitle && (
                        <Typography className="tile-title" {...styles.tileTitle}>
                            {fields.tileTitle}
                        </Typography>
                    )}
                    <Typography className="tile-copyText" {...styles.tileCopyText}>
                        {fields.tileText}
                    </Typography>
                    {(fields.buttonLabel || url) && (
                        <Button
                            className="cta-button"
                            {...styles.tileButton}
                            variant={fields.variant}
                            onClick={onClick}
                        >
                            {fields.buttonLabel || title || url}
                        </Button>
                    )}
                </StyledWrapper>
                <StyledWrapper className="image-section" {...styles.imageSection}>
                    <LazyImage
                        src={fields.image}
                        altText={fields.imageAlt || translate("Tile Image")}
                        {...styles.image}
                        className="img"
                        isShareEntity={true}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.wrapper {
}
.content-section {
}
.tile-title {
}
.tile-copyText {
}

.cta-button {
}

.image-section {
}

.img {
}


`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const imageTextTile: WidgetModule = {
    component: ImageTextTile,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.imageAlt,
                displayName: "Image Alt",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.image,
                tab: basicTab,
            },

            {
                fieldType: "Contextual",
                name: fields.imagePositioning,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Right", value: "right" },
                    { displayName: "Left", value: "left" },
                ],
                hideEmptyOption: true,
                defaultValue: "right",
                tab: basicTab,
            },

            {
                fieldType: "Contextual",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "#E7EBEF",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.tileTitle,
                editorTemplate: "MultilineTextField",
                displayName: "Title Heading Title",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.tileText,
                editorTemplate: "MultilineTextField",
                displayName: "Tile Text",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default imageTextTile;
